import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, CircleNotch, Download, Export, FolderOpen, Trash } from "@phosphor-icons/react";
import { CopyButton } from "@/components/shared";
import { API } from "@/lib/api";

// ============= CONTENT VAULT SECTION =============
const ContentVault = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ platform: "", category: "" });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const CATEGORIES = ["general", "educational", "promotional", "storytelling", "behind-the-scenes", "trending"];
  const PLATFORMS = ["tiktok", "instagram", "linkedin", "youtube-shorts", "youtube"];
  const POST_TYPES = ["reel", "story", "post", "short", "video"];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.platform) params.append("platform", filter.platform);
      if (filter.category) params.append("category", filter.category);
      const res = await axios.get(`${API}/vault?${params.toString()}`);
      setItems(res.data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/vault/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) { console.error(e); }
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/vault/${id}`, editForm);
      setEditingId(null);
      fetchItems();
    } catch (e) { console.error(e); }
  };

  const exportCSV = async () => {
    const ids = selected.size > 0 ? Array.from(selected) : items.map((i) => i.id);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API}/vault/export-csv`, ids, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "gohighlevel_bulk_posts.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); alert("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="content-vault-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-zinc-900">Content Vault</h2>
          <p className="text-sm text-zinc-500 mt-1">Save, categorize, and bulk export for GoHighLevel</p>
        </div>
        <button
          onClick={exportCSV}
          disabled={items.length === 0}
          className="btn-primary text-sm px-4 py-2 flex items-center gap-2 w-full sm:w-auto justify-center"
          data-testid="export-csv-btn"
        >
          <Export size={16} />
          Export CSV {selected.size > 0 ? `(${selected.size})` : `(All ${items.length})`}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filter.platform}
          onChange={(e) => setFilter((f) => ({ ...f, platform: e.target.value }))}
          className="input-field text-sm w-auto"
          data-testid="vault-filter-platform"
        >
          <option value="">All Platforms</option>
          {PLATFORMS.map((p) => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1).replace("-", " ")}</option>
          ))}
        </select>
        <select
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
          className="input-field text-sm w-auto"
          data-testid="vault-filter-category"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace("-", " ")}</option>
          ))}
        </select>
        {items.length > 0 && (
          <button
            onClick={selectAll}
            className="text-sm text-[#E91E8C] hover:underline font-medium"
            data-testid="select-all-btn"
          >
            {selected.size === items.length ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>

      {/* Items Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><CircleNotch size={24} className="animate-spin text-[#E91E8C]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen size={48} className="text-zinc-200 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No content saved yet. Create videos in Auto Create and save them here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className={`card overflow-hidden transition-all ${selected.has(item.id) ? "ring-2 ring-[#E91E8C]" : ""}`}
              data-testid={`vault-item-${item.id}`}
            >
              {/* Video Preview */}
              <div className="aspect-[9/16] max-h-[220px] bg-black relative">
                <video src={item.video_url} className="w-full h-full object-contain" muted />
                <button
                  onClick={() => toggleSelect(item.id)}
                  className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selected.has(item.id) ? "bg-[#E91E8C] border-[#E91E8C]" : "bg-white/80 border-zinc-300"
                  }`}
                >
                  {selected.has(item.id) && <Check size={12} className="text-white" weight="bold" />}
                </button>
              </div>

              {/* Info */}
              {editingId === item.id ? (
                <div className="p-3 space-y-2">
                  <input value={editForm.title || ""} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className="input-field text-sm" placeholder="Title" />
                  <textarea value={editForm.caption || ""} onChange={(e) => setEditForm((f) => ({ ...f, caption: e.target.value }))} className="input-field text-sm" rows={2} placeholder="Caption" />
                  <input value={editForm.hashtags || ""} onChange={(e) => setEditForm((f) => ({ ...f, hashtags: e.target.value }))} className="input-field text-sm" placeholder="#hashtags" />
                  <div className="grid grid-cols-2 gap-2">
                    <select value={editForm.platform || ""} onChange={(e) => setEditForm((f) => ({ ...f, platform: e.target.value }))} className="input-field text-sm">
                      {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <select value={editForm.category || ""} onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))} className="input-field text-sm">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <select value={editForm.post_type || "reel"} onChange={(e) => setEditForm((f) => ({ ...f, post_type: e.target.value }))} className="input-field text-sm">
                    {POST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input type="datetime-local" value={editForm.scheduled_date || ""} onChange={(e) => setEditForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="input-field text-sm" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(item.id)} className="btn-primary text-xs px-3 py-1.5 flex-1">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-xs px-3 py-1.5 text-zinc-500 hover:text-zinc-700">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <p className="text-sm font-semibold text-zinc-900 truncate">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs bg-[#E91E8C]/10 text-[#E91E8C] px-2 py-0.5 rounded-full font-medium">{item.platform}</span>
                    <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{item.category}</span>
                    <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{item.post_type}</span>
                  </div>
                  {item.caption && <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2">{item.caption}</p>}
                  <p className="text-xs text-zinc-400 mt-1">{new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>

                  <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-100">
                    <button
                      onClick={() => { setEditingId(item.id); setEditForm({ ...item }); }}
                      className="p-1.5 text-zinc-400 hover:text-[#E91E8C] rounded-lg hover:bg-zinc-50 text-xs"
                      data-testid={`edit-vault-${item.id}`}
                    >
                      Edit
                    </button>
                    <a href={item.video_url} target="_blank" rel="noopener noreferrer" download className="p-1.5 text-zinc-400 hover:text-[#E91E8C] rounded-lg hover:bg-zinc-50">
                      <Download size={14} />
                    </a>
                    <CopyButton text={item.video_url} label="" />
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="p-1.5 text-zinc-400 hover:text-red-500 rounded-lg hover:bg-zinc-50 ml-auto"
                      data-testid={`delete-vault-${item.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentVault;
